import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  IconButton,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';

import { useAppDispatch, useAppSelector } from '../store';
import { addEmployeeTag, replaceEmployeeTag, removeEmployeeTag } from '../store/employeeTagsSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { EmployeeTag, CreateEmployeeTagPayload, PatchEmployeeTagPayload } from '../types/employeeTags';
import NavBanglaPhoneticInput from './NavBanglaPhoneticInput';
import { FormNavigationProvider, NavTextField, NavButton } from './FormNavigation';

interface QuickGrouplessTagEditorDialogProps {
  open: boolean;
  onClose: () => void;
  rootTag: EmployeeTag | null;
  fieldTitle: string;
}

export default function QuickGrouplessTagEditorDialog({ open, onClose, rootTag, fieldTitle }: QuickGrouplessTagEditorDialogProps) {
  const dispatch = useAppDispatch();
  const allTags = useAppSelector((s) => s.employeeTags.tags);

  const containerRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selectedTag, setSelectedTag] = useState<EmployeeTag | null>(null);

  const [tagTitle, setTagTitle] = useState('');
  const [tagTitleBangla, setTagTitleBangla] = useState('');
  
  const [addAnother, setAddAnother] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Get only direct children of rootTag based on tags traversing
  const childTags = useMemo(() => {
    if (!rootTag) return [];
    return allTags.filter((t) => t.parentId === rootTag.tagId);
  }, [allTags, rootTag]);

  const filteredTags = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return childTags;
    return childTags.filter((t) => 
      t.tagTitle.toLowerCase().includes(q) || 
      (t.tagTitleBangla && t.tagTitleBangla.toLowerCase().includes(q))
    );
  }, [childTags, searchQuery]);

  // Reset form when opened or mode changed
  useEffect(() => {
    if (open) {
      handleCreateNew();
    }
  }, [open, rootTag]);

  const handleCreateNew = () => {
    setMode('create');
    setSelectedTag(null);
    setTagTitle('');
    setTagTitleBangla('');
    setError(null);
    setFieldErrors({});
  };

  const handleEditSelect = (tag: EmployeeTag) => {
    setMode('edit');
    setSelectedTag(tag);
    setTagTitle(tag.tagTitle);
    setTagTitleBangla(tag.tagTitleBangla ?? '');
    setError(null);
    setFieldErrors({});
  };

  const handleDeleteClick = (e: React.MouseEvent, tag: EmployeeTag) => {
    e.stopPropagation();
    if (tag.locked) {
      dispatch(showNotification({ message: `${fieldTitle} is in use and cannot be deleted.`, severity: 'info' }));
      return;
    }
    setSelectedTag(tag);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTag) return;

    setDeleting(true);
    setError(null);
    try {
      await apiService.deleteEmployeeTag(selectedTag.tagId);
      dispatch(removeEmployeeTag(selectedTag.tagId));
      dispatch(showNotification({ message: `${fieldTitle} deleted successfully`, severity: 'success' }));
      setDeleteConfirmOpen(false);
      handleCreateNew();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setFieldErrors(err.jsonMessage);
      } else {
        setError(err?.message ?? `Failed to delete ${fieldTitle}`);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!rootTag) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      if (mode === 'edit' && selectedTag) {
        const payload: PatchEmployeeTagPayload = {
          tagId: selectedTag.tagId,
          tagTitle: tagTitle || undefined,
          tagTitleBangla: tagTitleBangla || null,
        };
        const resp = await apiService.patchEmployeeTag(payload);
        dispatch(replaceEmployeeTag(resp.data));
        dispatch(showNotification({ message: `${fieldTitle} updated successfully`, severity: 'success' }));
        requestAnimationFrame(() => titleInputRef.current?.focus());
      } else {
        const payload: CreateEmployeeTagPayload = {
          tagTitle,
          tagTitleBangla: tagTitleBangla || null,
          parentId: rootTag.tagId,
        };
        const resp = await apiService.createEmployeeTag(payload);
        dispatch(addEmployeeTag(resp.data));
        dispatch(showNotification({ message: `${fieldTitle} created successfully`, severity: 'success' }));
        
        if (addAnother) {
          handleCreateNew();
        } else {
          // Automatically switch to edit mode for the newly created element
          handleEditSelect(resp.data);
        }
        // Clear search so it shows up in top or appropriate place
        setSearchQuery('');
        
        requestAnimationFrame(() => titleInputRef.current?.focus());
      }
    } catch (err: any) {
      if (err?.jsonMessage) {
        setFieldErrors(err.jsonMessage);
      } else {
        setError(err?.message ?? 'An error occurred');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          onClose();
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ ref: containerRef }}
      >
        <FormNavigationProvider containerRef={containerRef} submitButtonRef={submitButtonRef}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5, pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>{fieldTitle} Editor</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', height: 450 }}>
        {/* Left Sidebar */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.01)' }}>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleCreateNew} 
              sx={{ bgcolor: mode === 'create' ? 'primary.light' : 'transparent', color: mode === 'create' ? 'primary.contrastText' : 'primary.main', '&:hover': { bgcolor: mode === 'create' ? 'primary.main' : 'rgba(25,118,210,0.1)' } }}
              title="Add New Tag"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
            {filteredTags.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No tags found.</Typography>
              </Box>
            ) : (
              filteredTags.map((tag) => (
                <ListItemButton 
                  key={tag.tagId} 
                  selected={mode === 'edit' && selectedTag?.tagId === tag.tagId}
                  onClick={() => handleEditSelect(tag)}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', pr: 6 }}
                >
                  <ListItemText
                    primary={tag.tagTitle}
                    secondary={tag.tagTitleBangla}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: (mode === 'edit' && selectedTag?.tagId === tag.tagId) ? 600 : 400 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <IconButton
                    size="small"
                    color={tag.locked ? 'default' : 'error'}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                    onClick={(e) => handleDeleteClick(e, tag)}
                    title={tag.locked ? 'Locked' : 'Delete'}
                  >
                    {tag.locked ? <LockIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                  </IconButton>
                </ListItemButton>
              ))
            )}
          </List>
        </Box>

        {/* Right Content */}
        <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            {mode === 'create' ? 'Create New Element' : 'Edit Element'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <NavTextField
              label="Tag Title"
              inputRef={titleInputRef}
              fullWidth
              size="small"
              value={tagTitle}
              onChange={(e) => setTagTitle(e.target.value)}
              error={!!fieldErrors['tagTitle']}
              helperText={fieldErrors['tagTitle']}
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <NavBanglaPhoneticInput
              label="Tag Title (Bangla)"
              fullWidth
              size="small"
              value={tagTitleBangla}
              onChange={setTagTitleBangla}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {selectedTag?.locked && (
             <Alert severity="warning" sx={{ mt: 2, py: 0 }}>{fieldTitle} is in use and cannot be deleted.</Alert>
          )}

          {mode === 'create' && (
            <FormControlLabel
              control={<Switch size="small" checked={addAnother} onChange={(e) => setAddAnother(e.target.checked)} />}
              label={<Typography variant="body2">Add another</Typography>}
              sx={{ width: 'fit-content' }}
            />
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving || deleting}>Cancel</Button>
        <NavButton ref={submitButtonRef} onClick={handleSave} variant="contained" disabled={saving || deleting || !tagTitle.trim()} navSkip={false}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </NavButton>
      </DialogActions>
        </FormNavigationProvider>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete <strong>"{selectedTag?.tagTitle}"</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
          {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
